import { Component, Injectable, ViewChild } from '@angular/core';
import { ChangeDetectionStrategy } from '@angular/core';
import { Input } from '@angular/core';
import { ApplicationRef, ComponentRef, EnvironmentInjector, createComponent } from '@angular/core';

import { Subscription } from 'rxjs';

import { languageService, toastService } from '@unpispas/upp-base';
import { dataService } from '@unpispas/upp-data';
import { printService } from '@unpispas/feature/print';

import { OrderInfo } from '@unpispas/upp-data';
import { PrintInfo } from '@unpispas/upp-data';
import { PrintDevice } from '@unpispas/upp-data';
import { Pinpad } from '@unpispas/upp-data';

import { PlaceView } from '@unpispas/upp-data';
import { RasppiView } from '@unpispas/upp-data';
import { TicketView } from '@unpispas/upp-data';

/** Generic toast when a Paytef terminal print (receipt or voucher) fails. */
const TERMINAL_PRINT_FAILED_KEY = '@ticket_print_terminal_failed';

/**
 * Outcome of a receipt dispatch from {@link queueService.PrintTicket}.
 * `failure` is true when the terminal was tried and did not print (browser backup skips RECEIPT).
 */
type ReceiptDispatch = {
    route: 'BAR' | 'TERMINAL' | 'BROWSER';
    printed: boolean;
    failure: boolean;
};

import { UppTicketPrintComponent } from '../components/print/ticket/upp-print';
import { UppTicketOrderComponent } from '../components/print/order/upp-print';

/********************************/
/* Silent print/order host */
/********************************/

/**
 * Silent per-ticket slot: one receipt renderer + one order renderer.
 * Created only by {@link queueService} (refcount guarantees uniqueness).
 * Physical dispatch lives in the service, not in these children.
 */
@Component({
    selector: 'upp-print-queue',
    standalone: true,
    imports: [UppTicketPrintComponent, UppTicketOrderComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <upp-ticket-print #ticket_print [ticket]="ticket" [display]="false"></upp-ticket-print>
        <upp-ticket-order #ticket_order [ticket]="ticket" [display]="false"></upp-ticket-order>
    `,
})
class queueComponent {
    @Input() ticket: TicketView | null = null;

    @ViewChild('ticket_print') ticket_print: UppTicketPrintComponent | undefined;
    @ViewChild('ticket_order') ticket_order: UppTicketOrderComponent | undefined;
}

type QueueEntry = {
    refs: number;
    jobs: number;
    ticket: TicketView;
    /** Null until the first print/order/prepare needs renderers (lazy mount). */
    hostElement: HTMLElement | null;
    /** Null until {@link queueService._EnsureHost} mounts the silent slot. */
    componentRef: ComponentRef<queueComponent> | null;
    subscriptions: Subscription[];
};

/**
 * Ensures at most one silent print/order watch per ticket while edit/thumb
 * (or other screens) keep the ticket watched. Subscribes once to `OnPrint` /
 * `OnOrder`; mounts the hidden `queueComponent` only when a job actually needs
 * renderers (list thumbs can watch hundreds of tickets without DOM cost).
 *
 * `Unwatch` may run while a job is still async (edit navigates away right after
 * commit). The host is kept until in-flight jobs finish so kitchen orders are
 * not dropped mid-dispatch.
 */
@Injectable({
    providedIn: 'root',
})
export class queueService {
    private readonly _queue = new Map<TicketView, QueueEntry>();

    constructor(
        private readonly appRef: ApplicationRef,
        private readonly environmentInjector: EnvironmentInjector,
        private readonly print: printService,
        private readonly data: dataService,
        private readonly toast: toastService,
        private readonly lang: languageService,
    ) {}

    /********************************/
    /* PUBLIC API                   */
    /********************************/

    /**
     * Starts (or refcounts) the silent print/order watch for `ticket`.
     * Subscribes to `OnPrint` / `OnOrder`; the hidden render host mounts lazily
     * on the first job. Identity is the `TicketView` instance (model uniqueness).
     */
    Watch(ticket: TicketView | null | undefined): void {
        if (!ticket){
            return;
        }

        const _existing = this._queue.get(ticket);
        if (_existing){
            _existing.refs += 1;
            return;
        }

        this._queue.set(ticket, this._CreateWatch(ticket));
    }

    /**
     * Opens the silent receipt host for `ticket`, renders printable DOM, and
     * returns it for PDF/email. Caller must invoke `release()` when finished
     * so `IsPrinting` is cleared and the job refcount drops.
     */
    async PrepareReceiptElement(ticket: TicketView | null | undefined): Promise<{
        element: HTMLElement;
        release: () => void;
    } | null> {
        if (!ticket) {
            return null;
        }

        this.Watch(ticket);
        const _entry = this._queue.get(ticket);
        if (!_entry || !this._EnsureHost(_entry)) {
            this.Unwatch(ticket);
            return null;
        }

        const _receipt = _entry.componentRef?.instance.ticket_print;
        if (!_receipt) {
            this.Unwatch(ticket);
            return null;
        }

        this._AddJob(_entry);
        try {
            const _element = await _receipt.Printable();
            if (!_element) {
                this._DelJob(_entry);
                this.Unwatch(ticket);
                return null;
            }

            return {
                element: _element,
                release: () => {
                    _receipt.IsPrinting = false;
                    this._DelJob(_entry);
                    this.Unwatch(ticket);
                },
            };
        }
        catch (err) {
            console.error('[PRINT TICKET] PrepareReceiptElement failed', err);
            _receipt.IsPrinting = false;
            this._DelJob(_entry);
            this.Unwatch(ticket);
            return null;
        }
    }

    /**
     * Drops one watch reference. At zero refs, stops accepting new events; the
     * host is destroyed only when no print/order job is still running.
     */
    Unwatch(ticket: TicketView | null | undefined): void {
        if (!ticket){
            return;
        }

        const _entry = this._queue.get(ticket);
        if (!_entry){
            return;
        }

        _entry.refs -= 1;
        if (_entry.refs > 0){
            return;
        }

        for (const _sub of _entry.subscriptions){
            _sub.unsubscribe();
        }
        _entry.subscriptions = [];
        this._Release(_entry);
    }

    /**
     * Reprints a card voucher copy on the own Paytef terminal via {@link printService}.
     * No BAR/browser fallback and no RECEIPT activity. Failure shows a generic toast;
     * the waiter retries. Terminal FIFO lives in print (`RunOnTerminal` / voucher).
     */
    async PrintVoucher(ticket: TicketView | null | undefined, copy: 'merchant' | 'customer'): Promise<void> {
        if (!ticket){
            return;
        }

        const _terminal = ticket.place?.OwnPinpad ?? null;
        const _row = ticket.PinpadVoucher;
        if (!_terminal || !_row){
            return;
        }

        const _ok = await this.print.PrintVoucher(_terminal, _row, copy);
        if (!_ok){
            void this.toast.ShowAlert('warning', this.lang.tr(TERMINAL_PRINT_FAILED_KEY));
        }
    }

    /********************************/
    /* HOST LIFECYCLE               */
    /********************************/

    /**
     * Registers OnPrint/OnOrder without mounting renderers (list thumbs stay cheap).
     */
    private _CreateWatch(ticket: TicketView): QueueEntry {
        const _entry: QueueEntry = {
            refs: 1,
            jobs: 0,
            ticket,
            hostElement: null,
            componentRef: null,
            subscriptions: [],
        };

        if (ticket.OnPrint){
            _entry.subscriptions.push(ticket.OnPrint.subscribe((info: PrintInfo) => {
                void this.PrintTicket(_entry, info);
            }));
        }
        if (ticket.OnOrder){
            _entry.subscriptions.push(ticket.OnOrder.subscribe((info: OrderInfo) => {
                void this.PrintOrder(_entry, info);
            }));
        }

        return _entry;
    }

    /**
     * Mounts the silent print/order slot once, when a job needs DOM.
     * @returns false when ViewChild renderers are missing after create.
     */
    private _EnsureHost(entry: QueueEntry): boolean {
        if (entry.componentRef){
            return true;
        }

        const _hostElement = document.createElement('div');
        _hostElement.setAttribute('aria-hidden', 'true');
        _hostElement.setAttribute('data-upp-print-queue', '1');
        _hostElement.style.display = 'none';
        document.body.appendChild(_hostElement);

        const _componentRef = createComponent(queueComponent, {
            environmentInjector: this.environmentInjector,
            hostElement: _hostElement,
        });
        _componentRef.setInput('ticket', entry.ticket);
        this.appRef.attachView(_componentRef.hostView);
        _componentRef.changeDetectorRef.detectChanges();

        entry.hostElement = _hostElement;
        entry.componentRef = _componentRef;

        if (!_componentRef.instance.ticket_print || !_componentRef.instance.ticket_order){
            console.error('[PRINT QUEUE] silent host missing ticket_print/ticket_order');
            this._DestroyHost(entry);
            return false;
        }

        return true;
    }

    private _Release(entry: QueueEntry): void {
        if ((entry.refs > 0) || (entry.jobs > 0)){
            return;
        }

        this._Destroy(entry);
        this._queue.delete(entry.ticket);
    }

    private _Destroy(entry: QueueEntry): void {
        for (const _sub of entry.subscriptions){
            _sub.unsubscribe();
        }
        entry.subscriptions = [];
        this._DestroyHost(entry);
    }

    private _DestroyHost(entry: QueueEntry): void {
        if (entry.componentRef){
            this.appRef.detachView(entry.componentRef.hostView);
            entry.componentRef.destroy();
            entry.componentRef = null;
        }
        if (entry.hostElement){
            entry.hostElement.remove();
            entry.hostElement = null;
        }
    }

    /********************************/
    /* JOB TRACKING                 */
    /********************************/

    private _AddJob(entry: QueueEntry): void {
        entry.jobs += 1;
    }

    private _DelJob(entry: QueueEntry): void {
        entry.jobs -= 1;
        if (entry.jobs < 0){
            entry.jobs = 0;
        }
        this._Release(entry);
    }

    /********************************/
    /* PRINT DISPATCH               */
    /********************************/

    /**
     * Opens the receipt's print-render window for `ticket` and dispatches it.
     * Automatic receipts (`info.isauto` with an own terminal, i.e. ptf) always go to the own Paytef terminal, never to BAR
     * printers nor the browser, and a failure only raises a generic toast.
     * Manual receipts go BAR printers of the till → till Paytef terminal (ptf own / POS puesto) → browser, with the
     * browser as backup when the terminal fails. The terminal path checks the fiscal QR first.
     * Registers RECEIPT only when printed with no terminal failure (dispatch owns logging).
     */
    private async PrintTicket(entry: QueueEntry, info: PrintInfo): Promise<ReceiptDispatch | null> {
        this._AddJob(entry);

        if (!this._EnsureHost(entry)){
            this._DelJob(entry);
            return null;
        }

        // POS payments also carry `isauto`, but OwnPinpad stays null outside ptf.
        const _auto = (info.isauto === true) && ((entry.ticket.place?.OwnPinpad ?? null) !== null);
        const _terminal = this._ReceiptPinpad(entry.ticket, _auto);
        if (!_terminal){
            return this._DispatchReceipt(entry, _auto);
        }

        return this.print.RunOnTerminal(_terminal, () => this._DispatchReceipt(entry, _auto));
    }

    /**
     * Terminal that will receive this receipt, or null when it goes to BAR printers or the browser.
     * Must stay in sync with the routing in {@link _DispatchReceipt}.
     * Automatic → own {@link PlaceView.OwnPinpad}; manual without BAR →
     * session service pinpads (`place.service.pinpads`), preferring OwnPinpad when listed.
     */
    private _ReceiptPinpad(ticket: TicketView, auto: boolean): Pinpad | null {
        if (auto){
            return ticket.place?.OwnPinpad ?? null;
        }

        if (this._TargetPrinters(ticket, 'ticket').length){
            return null;
        }

        return this._ManualReceipt(ticket.place);
    }

    /**
     * Preferred terminal for a **manual** receipt when there is no BAR printer.
     * Prefers {@link PlaceView.OwnPinpad} when it belongs to `place.service.pinpads`;
     * otherwise the first of that list.
     */
    private _ManualReceipt(place: PlaceView | null | undefined): Pinpad | null {
        const _list = place?.service?.pinpads ?? [];
        if (_list.length === 0){
            return null;
        }

        const _own = place?.OwnPinpad ?? null;
        if (_own && _list.includes(_own)){
            return _own;
        }

        return _list[0];
    }

    /**
     * Renders and dispatches the receipt of `entry` (the job count was taken by {@link PrintTicket}
     * and is released here). `auto` is the automatic-receipt decision taken at request time.
     * Never rejects.
     */
    private async _DispatchReceipt(entry: QueueEntry, auto: boolean): Promise<ReceiptDispatch | null> {
        const _ticket = entry.ticket;
        const _receipt = entry.componentRef?.instance.ticket_print;
        if (!_receipt){
            console.error('[PRINT TICKET] receipt renderer missing');
            this._DelJob(entry);
            return null;
        }

        console.log('[PRINT TICKET] ticket ' + _ticket.series + ' ' + _ticket.invceno + ' -> sending to print');

        let _dispatch: ReceiptDispatch | null = null;
        try {
            if (auto){
                _dispatch = await this._PrintReceiptAuto(_ticket, _receipt);
                return _dispatch;
            }

            const _devices = this._TargetPrinters(_ticket, 'ticket');
            if (_devices.length){
                const _element = await _receipt.Printable();
                if (!_element){
                    console.error('[PRINT TICKET] receipt DOM missing after prepare');
                    return null;
                }

                const _results = await Promise.all(
                    _devices.map((device) => this.print.printTicket(_element, device)),
                );
                _dispatch = {
                    route: 'BAR',
                    printed: _results.every((result) => result),
                    failure: false,
                };
                return _dispatch;
            }

            const _terminal = this._ManualReceipt(_ticket.place);
            if (_terminal){
                const _sent = await this._SendToTerminal(_receipt, _terminal);
                if (!_sent){
                    return null;
                }

                if (_sent.ok){
                    _dispatch = { route: 'TERMINAL', printed: true, failure: false };
                    return _dispatch;
                }

                void this.toast.ShowAlert('warning', this.lang.tr(TERMINAL_PRINT_FAILED_KEY));
                const _browserOk = await this.print.printTicket(_sent.element, null);
                _dispatch = {
                    route: 'BROWSER',
                    printed: _browserOk,
                    failure: true,
                };
                return _dispatch;
            }

            const _element = await _receipt.Printable();
            if (!_element){
                console.error('[PRINT TICKET] receipt DOM missing after prepare');
                return null;
            }

            const _ok = await this.print.printTicket(_element, null);
            _dispatch = { route: 'BROWSER', printed: _ok, failure: false };
            return _dispatch;
        }
        catch (err) {
            console.error('[PRINT TICKET] OnPrint dispatch failed', err);
            return null;
        }
        finally {
            if (_dispatch?.printed && !_dispatch.failure){
                _receipt.LogReceipt();
            }
            _receipt.IsPrinting = false;
            const _route = _dispatch?.route ?? '?';
            const _ok = _dispatch?.printed === true;
            console.log(
                '[PRINT TICKET] ticket ' + _ticket.series + ' ' + _ticket.invceno +
                ' -> route=' + _route +
                (_dispatch?.failure ? ' terminal-failed' : '') +
                ' print ' + (_ok ? 'completed' : 'failed'),
            );
            this._DelJob(entry);
        }
    }

    /**
     * Automatic receipt: forced to the own terminal. A failure (including a terminal that
     * is no longer resolvable) raises a generic toast and neither falls back to the browser
     * nor registers RECEIPT; the waiter can reprint manually.
     */
    private async _PrintReceiptAuto(ticket: TicketView, receipt: UppTicketPrintComponent): Promise<ReceiptDispatch | null> {
        const _terminal = ticket.place?.OwnPinpad ?? null;
        if (!_terminal){
            console.error(
                '[PRINT TICKET] automatic receipt without own terminal for ' +
                ticket.series + ' ' + ticket.invceno,
            );
            void this.toast.ShowAlert('warning', this.lang.tr(TERMINAL_PRINT_FAILED_KEY));
            return { route: 'TERMINAL', printed: false, failure: true };
        }

        const _sent = await this._SendToTerminal(receipt, _terminal);
        if (!_sent){
            return null;
        }

        if (_sent.ok){
            return { route: 'TERMINAL', printed: true, failure: false };
        }

        void this.toast.ShowAlert('warning', this.lang.tr(TERMINAL_PRINT_FAILED_KEY));
        return { route: 'TERMINAL', printed: false, failure: true };
    }

    /**
     * Renders the receipt once and sends it to `terminal`. Returns null when the render is missing.
     */
    private async _SendToTerminal(receipt: UppTicketPrintComponent, terminal: Pinpad): Promise<{ element: HTMLElement; ok: boolean } | null> {
        const _element = await receipt.Printable();
        if (!_element){
            console.error('[PRINT TICKET] receipt DOM missing after prepare');
            return null;
        }

        const _ok = await this.print.printTicket(_element, terminal);
        return { element: _element, ok: _ok };
    }

    /**
     * Resolves order destinations first, then renders and dispatches one slip at a time
     * ({@link UppTicketOrderComponent.Printable} + `print.printTicket`): `Printable` mutates the
     * same render host in place for every printer, so a destination's job (serialize + physical
     * send) is fully awaited before the next printer is rendered, reusing the same DOM safely.
     */
    private async PrintOrder(entry: QueueEntry, info: OrderInfo): Promise<void> {
        this._AddJob(entry);

        if (!this._EnsureHost(entry)){
            this._DelJob(entry);
            return;
        }

        const _ticket = entry.ticket;
        const _order = entry.componentRef?.instance.ticket_order;
        if (!_order){
            console.error('[PRINT ORDER] order renderer missing');
            this._DelJob(entry);
            return;
        }

        console.log('[PRINT ORDER] ticket ' + _ticket.series + ' ' + _ticket.invceno + ' (' + info.action + ') -> sending to print');

        let _success = true;
        let _touched = false;
        
        try {
            for (const _printer of this._TargetPrinters(_ticket, 'order')){
                const _element = await _order.Printable(_printer, info.action);
                if (!_element){
                    continue;
                }

                const _lines = _order.DispatchLines || [];
                if (!await this.print.printTicket(_element, _printer)){
                    _success = false;
                    continue;
                }

                // Manual copy reprints must not consume pending delta or flip PrintedFlag
                if (info.action === 'cpy'){
                    continue;
                }

                for (const _line of _lines){
                    if (_line.GetNotified() !== 0){
                        _line.SetNotified();
                        _touched = true;
                    }
                }

                if (!_ticket.PrintedFlag){
                    _ticket.PrintedFlag = true;
                    _touched = true;
                }
            }

            // Persist notified / PrintedFlag after the ticket commit that fired OnOrder
            if (_touched){
                try {
                    await _ticket.object.DoCommit();
                }
                catch (err) {
                    _success = false;
                    console.error('[PRINT ORDER] persist notified/PrintedFlag failed', err);
                }
            }
        }
        catch (err) {
            _success = false;
            console.error('[PRINT ORDER] OnOrder dispatch failed', err);
        }
        finally {
            _order.IsPrinting = false;
            console.log('[PRINT ORDER] ticket ' + _ticket.series + ' ' + _ticket.invceno + ' (' + info.action + ') -> print ' + (_success ? 'completed' : 'failed'));
            this._DelJob(entry);
        }
    }

    /********************************/
    /* PRINTER RESOLUTION           */
    /********************************/

    /**
     * Resolves destination printers for a receipt (`ticket`) or order slip (`order`).
     *
     * - `ticket`: BAR printers of the till service only (empty → caller uses browser).
     * - `order`: every place-service printer; BAR limited to that till; BAR/GEN require
     *   `updates`, and BAR without `posudts` skips when the ticket session device is current.
     *   Line selection (BAR omits kitchen `KIT`) lives in the order mapper.
     */
    private _TargetPrinters(ticket: TicketView, target: 'ticket' | 'order'): PrintDevice[] {
        const _tillService = this._TillService(ticket);

        if (target === 'ticket'){
            if (!_tillService){
                return [];
            }

            const _bars: PrintDevice[] = [];
            for (const _printer of _tillService.printers){
                if (_printer.type === 'BAR'){
                    _bars.push(_printer);
                }
            }

            return _bars;
        }

        const _place = ticket.place;
        if (!_place){
            return [];
        }

        const _barAllowed = new Set<PrintDevice>();
        if (_tillService){
            for (const _printer of _tillService.printers){
                _barAllowed.add(_printer);
            }
        }

        const _printers: PrintDevice[] = [];
        for (const _service of _place.services){
            for (const _printer of _service.printers){
                if ((_printer.type === 'BAR') && !_barAllowed.has(_printer)){
                    continue;
                }

                if ((_printer.type === 'BAR') || (_printer.type === 'GEN')){
                    if (_printer.updates !== true){
                        continue;
                    }

                    if ((_printer.type === 'BAR') && (_printer.posudts !== true)){
                        const _sessionDevice = ticket.object.session?.device;
                        if (_sessionDevice?.IsCurrent){
                            continue;
                        }
                    }
                }

                _printers.push(_printer);
            }
        }

        return _printers;
    }

    /** Till service used to authorize BAR printers for receipt caja and order dispatch. */
    private _TillService(ticket: TicketView): RasppiView | null {
        const _place = ticket.place;
        if (!_place){
            return null;
        }

        if (_place.service){
            return _place.service;
        }

        const _tableTill = ticket.qrcode?.till;
        if (_tableTill?.rasppi){
            return _tableTill.rasppi;
        }

        const _sessionDevice = ticket.object.session?.device;
        if (_sessionDevice){
            for (const _service of _place.services){
                for (const _connect of _service.connected){
                    if (_connect.device === _sessionDevice){
                        return _service;
                    }
                }
            }
        }

        return null;
    }
}
